import React, { useState, useEffect, useRef, useCallback } from 'react';
import Modal from '../common/Modal';
import videoService from '../../services/videoService';
import { formatVideoRecordedAt, isHealthVideo, videoDisplayLabel } from '../../utils/videoDisplay';
import { logVideo, resolveVideoId } from '../../utils/videoDebug';
import {
  getPrefetchedStream,
} from '../../utils/videoPrefetchCache';
import {
  isEventVideoReview,
} from '../../modules/sensor-data/utils/videoRowSeverity';

const REFRESH_BUFFER_MS = 5 * 60 * 1000;
const MIN_STREAM_REFRESH_MS = 60 * 1000;
const VIDEO_LOAD_TIMEOUT_MS = 30000;

function formatFileSize(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getVideoErrorMessage(err) {
  const msg = err?.message || '';
  if (/503|not configured|unavailable/i.test(msg)) return 'Video service unavailable';
  if (/404|no matching|not found/i.test(msg)) return 'No data logger matches this video';
  if (/transcod/i.test(msg)) return msg;
  if (/timed out|preparation/i.test(msg)) return msg;
  if (/too many requests|rate limit/i.test(msg)) return 'Too many requests. Please wait and try again.';
  return msg || 'Failed to load video';
}

function getMediaErrorMessage(el) {
  if (!el?.error) return 'Video failed to load from cloud storage';
  switch (el.error.code) {
    case MediaError.MEDIA_ERR_ABORTED:
      return 'Video playback was aborted';
    case MediaError.MEDIA_ERR_NETWORK:
      return 'Network error while loading video (check S3 access / CORS)';
    case MediaError.MEDIA_ERR_DECODE:
      return 'Video file could not be decoded';
    case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
      return 'Video format not supported in browser';
    default:
      return 'Video failed to load from cloud storage';
  }
}

function VideoTypeBadge({ video }) {
  const label = videoDisplayLabel(video);
  const isKeepAlive = video?.videoLabel === 'keep_alive';
  const isEvent = video?.type === 'event' && !isKeepAlive;
  const className = [
    'video-type-badge',
    isKeepAlive ? 'video-type-badge--keep-alive' : '',
    isEvent ? 'video-type-badge--event' : '',
    !isKeepAlive && !isEvent ? 'video-type-badge--health' : '',
  ].filter(Boolean).join(' ');

  return <span className={className}>{label}</span>;
}

export default function VideoPlayerModal({ video, bridgeId, timeZone, onClose, onDeleted, canDelete }) {
  const [streamUrl, setStreamUrl] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [apiLoading, setApiLoading] = useState(true);
  const [preparing, setPreparing] = useState(false);
  const [buffering, setBuffering] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const videoRef = useRef(null);
  const refreshTimerRef = useRef(null);
  const loadTimeoutRef = useRef(null);
  const openRef = useRef(true);
  const streamFetchInFlightRef = useRef(false);

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const clearLoadTimeout = useCallback(() => {
    if (loadTimeoutRef.current) {
      clearTimeout(loadTimeoutRef.current);
      loadTimeoutRef.current = null;
    }
  }, []);

  const loadStream = useCallback(async (resumeTime) => {
    const videoId = resolveVideoId(video);
    logVideo('play — open modal', { videoId, bridgeId, filename: video?.filename });

    if (!videoId) {
      setApiLoading(false);
      setPreparing(false);
      setError('Video has no id — backend must return _id for playback');
      return;
    }

    if (streamFetchInFlightRef.current) return;
    streamFetchInFlightRef.current = true;

    setApiLoading(true);
    setPreparing(false);
    setError(null);
    setBuffering(false);

    try {
      const cached = getPrefetchedStream(videoId, video?.impactAudioGateVersion);
      if (cached?.streamUrl && (video?.type !== 'event' || cached.audioGated === true)) {
        logVideo('play — using prefetched stream URL', { videoId });
        setStreamUrl(cached.streamUrl);
        setExpiresAt(cached.expiresAt);
        setApiLoading(false);
        setPreparing(false);
        setBuffering(true);
        if (resumeTime != null && videoRef.current) {
          videoRef.current.currentTime = resumeTime;
        }
        return;
      }

      const initial = await videoService.getStreamUrl(videoId);
      if (!openRef.current) return;

      let res = initial;
      if (initial.streamUrl && initial.httpStatus !== 202) {
        res = initial;
      } else if (
        initial.httpStatus === 202
        || initial.transcodeStatus === 'pending'
        || initial.transcodeStatus === 'processing'
        || !initial.streamUrl
      ) {
        if (video?.type !== 'event') {
          setApiLoading(false);
          setPreparing(true);
        }
        logVideo('play — transcode in progress, polling', {
          httpStatus: initial.httpStatus,
          transcodeStatus: initial.transcodeStatus,
        });
        res = await videoService.getPlaybackStream(videoId, () => openRef.current);
      }

      if (!openRef.current) return;

      const url = res.streamUrl;
      if (!url) {
        throw new Error(res.message || 'Could not get playback URL');
      }

      logVideo('play — playback URL OK', {
        codec: res.codec || res.playbackCodec,
        expiresAt: res.expiresAt,
        urlPrefix: `${url.slice(0, 80)}...`,
      });

      setStreamUrl(url);
      setExpiresAt(res.expiresAt);
      setApiLoading(false);
      setPreparing(false);
      setBuffering(true);

      if (resumeTime != null && videoRef.current) {
        videoRef.current.currentTime = resumeTime;
      }
    } catch (err) {
      if (!openRef.current) return;
      logVideo('play — failed', { message: err?.message });
      setStreamUrl(null);
      setError(getVideoErrorMessage(err));
      setApiLoading(false);
      setPreparing(false);
      setBuffering(false);
    } finally {
      streamFetchInFlightRef.current = false;
    }
  }, [video, bridgeId]);

  useEffect(() => {
    openRef.current = true;
    loadStream();
    return () => {
      openRef.current = false;
      clearRefreshTimer();
      clearLoadTimeout();
    };
  }, [loadStream, clearRefreshTimer, clearLoadTimeout]);

  useEffect(() => {
    clearRefreshTimer();
    if (!expiresAt || !streamUrl || preparing || apiLoading) return;

    const expiryMs = new Date(expiresAt).getTime();
    if (Number.isNaN(expiryMs)) return;

    const refreshAt = expiryMs - REFRESH_BUFFER_MS;
    let delay = Math.max(refreshAt - Date.now(), MIN_STREAM_REFRESH_MS);

    refreshTimerRef.current = setTimeout(() => {
      if (streamFetchInFlightRef.current) return;
      const currentTime = videoRef.current?.currentTime ?? 0;
      loadStream(currentTime);
    }, delay);

    return clearRefreshTimer;
  }, [expiresAt, streamUrl, preparing, apiLoading, loadStream, clearRefreshTimer]);

  const handleVideoError = useCallback(() => {
    const el = videoRef.current;
    const mediaMsg = getMediaErrorMessage(el);
    logVideo('play — video element error', { mediaMsg, code: el?.error?.code });
    setBuffering(false);
    setError(mediaMsg);
  }, []);

  const handleCanPlay = useCallback(() => {
    clearLoadTimeout();
    setBuffering(false);
    setError(null);
  }, [clearLoadTimeout]);

  const handleWaiting = useCallback(() => {
    setBuffering(true);
  }, []);

  useEffect(() => {
    clearLoadTimeout();
    if (!streamUrl || apiLoading || preparing) return;

    loadTimeoutRef.current = setTimeout(() => {
      const el = videoRef.current;
      if (!el || el.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) return;
      logVideo('play — load timeout', { readyState: el.readyState });
      handleVideoError();
    }, VIDEO_LOAD_TIMEOUT_MS);

    return clearLoadTimeout;
  }, [streamUrl, apiLoading, preparing, clearLoadTimeout, handleVideoError]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.setAttribute('controlslist', 'nodownload');
    if (el.controlsList?.add) el.controlsList.add('nodownload');
  }, [streamUrl]);

  const handleDownload = async () => {
    const videoId = resolveVideoId(video);
    if (!videoId || downloading) return;
    setDownloading(true);
    try {
      const res = await videoService.getDownloadUrl(videoId);
      window.open(res.downloadUrl || res.streamUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(getVideoErrorMessage(err));
    } finally {
      setDownloading(false);
    }
  };

  const titleSuffix = video?.type === 'event'
    ? (isEventVideoReview(video) ? 'Event Video (review)' : 'Event Video')
    : 'Health Video';

  const healthVideo = isHealthVideo(video);
  const modalTitle = healthVideo
    ? (video?.dataLoggerSerialNo || 'Video')
    : `${video?.dataLoggerSerialNo || 'Video'} — ${titleSuffix}`;
  const showTypeBadge = video?.videoLabel !== 'keep_alive';
  const showDownload = !healthVideo;
  const showToolbar = showTypeBadge || showDownload || canDelete;

  const statusMessage = preparing && video?.type !== 'event'
    ? 'Preparing video…'
    : null;

  return (
    <Modal
      title={modalTitle}
      onClose={onClose}
      width={640}
    >
      <div className="video-player-modal">
        {showToolbar && (
        <div className="video-player-toolbar">
          {showTypeBadge && <VideoTypeBadge video={video} />}
          <div style={{ flex: 1 }} />
          {showDownload && (
            <button
              type="button"
              className="btn btn-sm sensor-video-icon-btn"
              style={{ background: '#fff', color: '#333' }}
              title="Download"
              disabled={!resolveVideoId(video) || downloading || apiLoading || preparing}
              onClick={handleDownload}
            >
              <i className="ti ti-download" />
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              className="btn btn-sm sensor-video-icon-btn"
              style={{ background: '#c00', color: '#fff' }}
              title="Delete"
              disabled={!resolveVideoId(video)}
              onClick={() => onDeleted?.(video)}
            >
              <i className="ti ti-trash" />
            </button>
          )}
        </div>
        )}

        <div className="video-player-frame">
          {statusMessage && (
            <div className="video-player-status">{statusMessage}</div>
          )}
          {!apiLoading && !preparing && error && !streamUrl && (
            <div className="video-player-status video-player-status--error">{error}</div>
          )}
          {!apiLoading && !preparing && streamUrl && (
            <>
              <video
                ref={videoRef}
                key={streamUrl}
                src={streamUrl}
                controls
                controlsList="nodownload"
                disablePictureInPicture={false}
                playsInline
                preload="auto"
                style={{ width: '100%', maxHeight: 360, display: 'block', background: '#000' }}
                onContextMenu={(event) => event.preventDefault()}
                onCanPlay={handleCanPlay}
                onLoadedData={handleCanPlay}
                onWaiting={handleWaiting}
                onPlaying={() => setBuffering(false)}
                onError={handleVideoError}
              />
              {buffering && !error && (
                <div className="video-player-buffering">Buffering…</div>
              )}
              {error && (
                <div className="video-player-status video-player-status--error video-player-status--overlay">
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        <div className="video-player-meta">
          <div><strong>Recorded:</strong> {formatVideoRecordedAt(video?.recordedAt, timeZone)}</div>
          <div><strong>Size:</strong> {formatFileSize(video?.fileSize)}</div>
        </div>
      </div>
    </Modal>
  );
}
